import React, { useEffect, useRef, useState } from 'react';
import './ConnectionBanner.css';

/**
 * Tells you when the browser has lost its live link to the server.
 *
 * Until now this only existed as a small chip in the page header — which is hidden on phones
 * and in fullscreen, exactly the two places the live map is usually watched. A stalled socket
 * therefore looked identical to quiet airspace: the aircraft simply stop moving, and nothing
 * on screen says why.
 *
 * Deliberately not dismissible. While the socket is down everything on screen is stale, and a
 * dismissed warning would leave the map looking authoritative while it silently ages.
 */

/**
 * How long a bad status must persist before we say anything.
 *
 * The socket drops and re-establishes routinely — a reconnect inside a couple of seconds is
 * normal and invisible to the data. Announcing those would train people to ignore the banner,
 * which is worse than not having one.
 */
const GRACE_MS = 3000;

/** How long "Reconnected" stays up before getting out of the way. */
const SETTLE_MS = 4000;

function ConnectionBanner({ status }) {
  // 'hidden' | 'lost' | 'restored'
  const [mode, setMode] = useState('hidden');
  const everConnected = useRef(false);
  const wasLost = useRef(false);

  useEffect(() => {
    if (status === 'connected') {
      everConnected.current = true;
      if (wasLost.current) {
        wasLost.current = false;
        setMode('restored');
        const t = setTimeout(() => setMode('hidden'), SETTLE_MS);
        return () => clearTimeout(t);
      }
      setMode('hidden');
      return undefined;
    }

    // First load is 'connecting' for everyone; calling that a lost connection would greet every
    // visitor with an error. Only complain about a link that existed and then didn't.
    if (!everConnected.current) return undefined;

    const t = setTimeout(() => {
      wasLost.current = true;
      setMode('lost');
    }, GRACE_MS);
    return () => clearTimeout(t);
  }, [status]);

  if (mode === 'hidden') return null;

  const restored = mode === 'restored';
  return (
    <div
      className={`conn-banner ${restored ? 'restored' : 'lost'}`}
      role="status"
      aria-live="polite"
    >
      <span className="conn-banner-dot" aria-hidden="true" />
      <span className="conn-banner-text">
        {restored ? (
          'Reconnected — live again'
        ) : (
          <>
            <strong>Connection lost.</strong> Reconnecting… what you see is no longer updating.
          </>
        )}
      </span>
      {!restored && (
        <button className="conn-banner-action" onClick={() => window.location.reload()}>
          Reload
        </button>
      )}
    </div>
  );
}

export default ConnectionBanner;
